import { cloneDeep, mapValues } from 'lodash';
import differenceWith from 'lodash/differenceWith';
import groupBy from 'lodash/groupBy';

import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

import {
    EditableRequirementCriterion,
} from '@/modules/decisioning-criteria/models/EditableRequirements.model';
import { castValueToMatchSchema } from '@/modules/decisioning-criteria/utils/syncCriteriaWithTemplates';

export function groupEditableCriteriaByType(
    criteria: EditableRequirementCriterion[],
): Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>> {
    return groupBy(criteria, 'coverageType') as Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>>;
}

export function groupTemplatesByType(
    templates: CriterionTemplate[],
): Partial<Record<TprmRequirementType, CriterionTemplate[]>> {
    return groupBy(templates, 'coverageType') as Partial<Record<TprmRequirementType, CriterionTemplate[]>>;
}

export function mapTemplatesByField(
    templatesByType: Partial<Record<TprmRequirementType, CriterionTemplate[]>>,
): Partial<Record<TprmRequirementType, Record<string, CriterionTemplate>>> {
    return Object.entries(templatesByType).reduce((acc, [ requirementType, templates ]) => ({
        ...acc,
        [requirementType]: (templates || []).reduce((templateAcc, template) => ({
            ...templateAcc,
            [template.field]: template,
        }), {}),
    }), {});
}

export function isEditableCriterionTypeAndFieldEqual(
    a: EditableRequirementCriterion,
    b: EditableRequirementCriterion,
): boolean {
    return a.coverageType === b.coverageType && a.field === b.field;
}

export function createDefaultEditableCriteriaForRequirementType(
    requirementType: TprmRequirementType,
    templatesByType: Partial<Record<TprmRequirementType, CriterionTemplate[]>>,
): EditableRequirementCriterion[] {
    return (templatesByType[requirementType] || [])
        .filter((template) => template.default)
        .map((template) => ({
            coverageType: template.coverageType,
            field: template.field,
            references: mapValues(
                template.evaluator.references,
                (ref) => castValueToMatchSchema(cloneDeep(ref.value), ref),
            ),
            verify: template.verify,
            emailMessage: template.emailMessage,
            deprecated: template.deprecated,
        }));
}

export function setEditableCriteriaVerifyState(
    criteria: EditableRequirementCriterion[],
    verify: boolean,
): EditableRequirementCriterion[] {
    return criteria.map((criterion) => ({
        ...criterion,
        verify,
    }));
}

export function removeRequirementTypeFromEvaluationRule(
    requirementType: TprmRequirementType,
    evaluationRuleRequirementTypes: TprmRequirementType[],
): TprmRequirementType[] {
    return evaluationRuleRequirementTypes.filter((type) => type !== requirementType);
}

export function tryAutoAddUmbrellaRequirementType(
    params: {
        criteriaByRequirementType: Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>>;
        requirementType: TprmRequirementType;
        currentCriteria: EditableRequirementCriterion[];
        originalCriteria: EditableRequirementCriterion[];
        canAutoAddUmbrella: boolean;
        umbrellaRequirementType: TprmRequirementType;
        umbrellaCriteria: EditableRequirementCriterion[];
    },
): Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>> {
    const {
        criteriaByRequirementType,
        requirementType,
        currentCriteria,
        originalCriteria,
        canAutoAddUmbrella,
        umbrellaRequirementType,
        umbrellaCriteria,
    } = params;

    const hasUmbrellaCriterion = Object.values(criteriaByRequirementType)
        .flat()
        .filter((criterion): criterion is EditableRequirementCriterion => Boolean(criterion))
        .some((criterion) => criterion.coverageType === umbrellaRequirementType);

    if (!canAutoAddUmbrella || hasUmbrellaCriterion || requirementType === umbrellaRequirementType) {
        return criteriaByRequirementType;
    }

    const criteriaWithAllowUmbrella = currentCriteria
        .filter((criterion) => Boolean(criterion.references.allowUmbrellaExcess));
    const newCriteriaWithAllowUmbrella = differenceWith(
        criteriaWithAllowUmbrella,
        originalCriteria,
        isEditableCriterionTypeAndFieldEqual,
    );
    const criteriaWithChangedAllowUmbrellaTrue = criteriaWithAllowUmbrella.filter(
        (criterion) => originalCriteria.find(
            (original) =>
                isEditableCriterionTypeAndFieldEqual(criterion, original) &&
                original.references.allowUmbrellaExcess === false &&
                criterion.references.allowUmbrellaExcess === true,
        ),
    );

    if (newCriteriaWithAllowUmbrella.length === 0 && criteriaWithChangedAllowUmbrellaTrue.length === 0) {
        return criteriaByRequirementType;
    }

    return {
        ...criteriaByRequirementType,
        [umbrellaRequirementType]: umbrellaCriteria,
    };
}
